import { useState } from "react";
import { useCurrentAccess } from "../../../../redux/api/org-user/orgUserAPI";
import {
  ISiteTag,
  useSiteFacets,
} from "../../../../redux/api/site-classification/siteClassificationAPI";
import AppButton from "../../../../shared/oversight-core/ui-elements/buttons/app-button/app-button";
import { useCanOperate } from "../../../../shared/utils/org-roles";
import {
  labelSiteTags,
  SECTOR_KEY,
  sectorLabelOf,
} from "../../../../shared/utils/site-tags";
import EditSiteTagsModal from "./edit-site-tags-modal";

interface IProps {
  plantId: string;
  siteTags: ISiteTag[];
  onSaved: () => void;
}

const chipClassName =
  "badge rounded-pill bg-white text-dark border font-size-12 fw-normal px-3 py-2";

// How the organization classifies this site - sector, region and the like - with editing for
// whoever runs the site. Shown for organization users; the vocabulary is per organization.
const SiteTagsBar = (props: IProps) => {
  const { plantId, siteTags, onSaved } = props;
  const [showEdit, setShowEdit] = useState(false);
  const canOperate = useCanOperate();
  const { data: currentAccess } = useCurrentAccess();
  const tenantId =
    currentAccess && !currentAccess.platformAdmin
      ? currentAccess.tenantId
      : undefined;
  const { data } = useSiteFacets(tenantId);

  if (!tenantId || !data) {
    return null;
  }

  const facets = data.siteFacets;
  const labelled = labelSiteTags(facets, siteTags);
  // Only when the sector facet is in the list: it is left out for guests when hidden from them,
  // and then a missing sector means "not shown", not "not set".
  const isUnclassified =
    facets.some((facet) => facet.standardKey === SECTOR_KEY) &&
    !sectorLabelOf(facets, siteTags);

  return (
    <div className="d-flex flex-wrap align-items-center gap-2">
      {isUnclassified && (
        <span className={`${chipClassName} text-light`}>Unclassified</span>
      )}
      {labelled.map((tag) => (
        <span key={tag.facetId} className={chipClassName}>
          <span className="text-light">{tag.facetName}:</span>{" "}
          {tag.labels.join(", ")}
        </span>
      ))}
      {canOperate && (
        <AppButton
          text="Edit tags"
          size="extra-small"
          variant="transparentWithBorder"
          iconName="edit"
          onClick={() => setShowEdit(true)}
        />
      )}
      {canOperate && (
        <EditSiteTagsModal
          show={showEdit}
          plantId={plantId}
          facets={facets}
          siteTags={siteTags}
          onClose={() => setShowEdit(false)}
          onSaved={onSaved}
        />
      )}
    </div>
  );
};

export default SiteTagsBar;
